import React, { useState, useEffect, useRef, FC, ComponentType } from "react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import heroPropertyImage from "@/assets/hero-property.jpg";
import { Upload, Trash2 } from "lucide-react";

interface LoggedUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  image?: string;
}
interface Property {
  id: number;
  propertyListType?: { id: number; list_name: string };
  title: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  agent?: { name: string; phone: string; email: string };
  is_open_house?: boolean | null;
}
interface PropertyList {
  id: number;
  list_name: string;
}
interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

export interface FlyerData {
  price: number | null;
  address: string;
  state?: string;
  city?: string;
  bedrooms: number | null;
  bathrooms: number | null;
  area: number | null;
  description: string;
  text_section: string;
  headline: string;
  features: { id?: number; feature_name: string }[];
  heroImage: string;
  secondaryImage?: string;
  photos?: string[];
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  list_name?: string;
}

export interface FlyerTemplateProps {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (fields: Partial<FlyerData>) => void;
  onPropertyListChange?: (list: string) => void;
  onPropertyListCreated?: (newList: { id: number; list_name: string }) => void;
  onPropertyListsUpdate?: (lists: { id: number; list_name: string }[]) => void;
  readOnly?: boolean;
  view: ComponentType<ViewProps>;
}

export interface ViewProps extends FlyerTemplateProps {
  data: FlyerData;
  selectedList: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onInputChange: (field: keyof FlyerData, value: any) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onAddFeature: () => void;
  onFeatureChange: (idx: number, value: string) => void;
  onFeatureBlur: (idx: number) => void;  // ✅ ADD THIS
  onFeatureDelete: (idx: number) => void;
  onListChange: (newList: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  readOnly?: boolean;
  isOpenHouseConfirmed: boolean;
  onOpenHouseConfirm: (confirmed: boolean) => Promise<void>;
  onPropertyListCreated?: (newList: { id: number; list_name: string }) => void;
  onPropertyListsUpdate?: (lists: { id: number; list_name: string }[]) => void;
}

const sanitize = (v?: string | null) =>
  String(v ?? "")
    .replace(/\r?\n|\r/g, " ")
    .trim();

const FlyerTemplate: FC<FlyerTemplateProps> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  onPropertyListCreated,
  onPropertyListsUpdate,
  readOnly = false,
  view: View,
}) => {
  const { toast } = useToast();
  const loggedUser: LoggedUser = JSON.parse(
    localStorage.getItem("user") || "{}"
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [localPropertyLists, setLocalPropertyLists] = useState<PropertyList[]>(propertyLists);

  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  const initialFeatures = property
    ? keyFeatures
        .filter((f) => f.propetyId === Number(property.id))
        .map((f) => ({
          id: f.id,
          feature_name: f.feature_name,
        }))
    : [];

  const [data, setData] = useState<FlyerData>({
    price: property?.price ?? null,
    address: sanitize(property?.address),
    state: property?.state ?? "",
    city: property?.city ?? "",
    bedrooms: property?.bedrooms ?? null,
    bathrooms: property?.bathrooms ?? null,
    area: property?.area ?? null,
    description: property?.description ?? "",
    text_section: property?.text_section ?? "",
    headline: property?.title ?? "",
    features: initialFeatures,
    heroImage: property?.image || heroPropertyImage,
    agentName: property?.agentName || property?.agent?.name || "",
    agentPhone: property?.agentPhone || property?.agent?.phone || "",
    agentEmail: property?.agentEmail || property?.agent?.email || "",
    list_name: property?.propertyListType?.list_name ?? "",
  });

  const [selectedList, setSelectedList] = useState<string>(
    property?.propertyListType?.list_name || ""
  );

  useEffect(() => {
    if (!property) return;

    setSelectedList(property.propertyListType?.list_name ?? "");
    setIsOpenHouseConfirmed(property.is_open_house ?? false);
    
    // Only update features from backend if property changed
    const backendFeatures = keyFeatures
      .filter((f) => f.propetyId === Number(property.id))
      .map((f) => ({ id: f.id, feature_name: f.feature_name }));

    setData((prev) => ({
      ...prev,
      price: property.price ?? prev.price,
      address: sanitize(property.address) || prev.address,
      state: property.state ?? prev.state,
      city: property.city ?? prev.city,
      bedrooms: property.bedrooms ?? prev.bedrooms,
      bathrooms: property.bathrooms ?? prev.bathrooms,
      area: property.area ?? prev.area,
      description: property.description !== undefined ? (property.description ?? "") : prev.description,
text_section: property.text_section !== undefined ? (property.text_section ?? "") : prev.text_section,
      headline: property.title ?? prev.headline,
      heroImage: property.image || prev.heroImage,
      features: backendFeatures,
      agentName: property.agentName ?? property.agent?.name ?? prev.agentName,
      agentPhone: property.agentPhone ?? property.agent?.phone ?? prev.agentPhone,
      agentEmail: property.agentEmail ?? property.agent?.email ?? prev.agentEmail,
    }));
  }, [property?.id, property?.description, property?.text_section]); // Only trigger on property ID change

  const handleOpenHouseConfirm = async (confirmed: boolean) => {
    if (!property?.id) return;

    setIsOpenHouseConfirmed(confirmed);
    onDirtyChange?.(true);

    try {
      await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
        is_open_house: confirmed,
      });
      toast({
        title: "Updated",
        description: confirmed
          ? "Open House promotion enabled"
          : "Open House promotion disabled",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update Open House status",
        variant: "destructive",
      });
      setIsOpenHouseConfirmed(!confirmed);
    }
  };

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const res = await axiosInstance.get(endpoints.me);
        const { first_name, last_name, phone_number, email } = res.data;
        const fullName = `${first_name ?? ""} ${last_name ?? ""}`.trim();
        setData((prev) => ({
          ...prev,
          agentName: prev.agentName || fullName || "No agent assigned",
          agentPhone: prev.agentPhone || phone_number || "N/A",
          agentEmail: prev.agentEmail || email || "N/A",
        }));

        onChange?.({
          agentName: fullName || "No agent assigned",
          agentPhone: phone_number || "N/A",
          agentEmail: email || "N/A",
        });
      } catch (err) {
        console.error("Error fetching agent data:", err);
        const fullName = loggedUser?.first_name
          ? `${loggedUser.first_name} ${loggedUser.last_name ?? ""}`.trim()
          : "No agent assigned";
        setData((prev) => ({
          ...prev,
          agentName: prev.agentName || fullName,
          agentPhone: prev.agentPhone || (loggedUser as any).phone_number || "N/A",
          agentEmail: prev.agentEmail || loggedUser?.email || "N/A",
        }));
      }
    };
    if (!data.agentName && !data.agentPhone && !data.agentEmail) {
      fetchAgent();
    }
  }, []);

const handleInput = (field: keyof FlyerData, value: any) => {
  let normalized = value;

  if (field === "price") {
    normalized = value === "" ? null : Number(String(value).replace(/[^0-9]/g, "")) || null;
  }
  if (["bedrooms", "bathrooms", "area"].includes(field)) {
    normalized = value === "" ? null : Number(value);
  }

  setData((prev) => {
    const next = { ...prev, [field]: normalized };
    onDirtyChange?.(true);
    
    // ✅ Fix: agentName mapping was overwriting — use clean mapping
   const mappedFields: Partial<FlyerData> =
  field === "agentName"
    ? { agentName: normalized }
    : { [field]: normalized };
    onChange?.(mappedFields);
    return next;
  });
};

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      setData((p) => ({ ...p, heroImage: url }));
      onChange?.({ heroImage: url });
      onDirtyChange?.(true);
    };
    reader.readAsDataURL(file);
  };

  const addFeature = () => {
    setData((p) => {
      // ✅ CHECK LIMIT
      if (p.features.length >= 10) {
        toast({
          title: "Feature Limit Reached",
          description: "You can add a maximum of 10 features per property.",
          variant: "destructive",
        });
        return p; // Don't add new feature
      }
      
      const features = [...p.features, { feature_name: "" }];
      return { ...p, features };
    });
  };

  const changeFeature = (idx: number, val: string) => {
    setData((p) => {
      const features = [...p.features];
      features[idx] = { ...features[idx], feature_name: val };
      
      // Only mark dirty if there's actual content
      if (val.trim() !== "") {
        onDirtyChange?.(true);
      }
      
      return { ...p, features };
    });
  };

  const blurFeature = (idx: number) => {
    setData((p) => {
      const feature = p.features[idx];
      
      // If feature is empty and unsaved, remove it silently
      if (!feature.feature_name.trim() && !feature.id) {
        const features = p.features.filter((_, i) => i !== idx);
        return { ...p, features };
      }
      
      // If feature has content, save it
      if (feature.feature_name.trim()) {
        onChange?.({ features: p.features });
      }
      
      return p;
    });
  };

  const deleteFeature = (idx: number) => {
    setData((p) => {
      const featureToDelete = p.features[idx];
      const features = p.features.filter((_, i) => i !== idx);
      
      // Only mark dirty and call onChange if deleting a saved or non-empty feature
      const hadContent = featureToDelete?.id || featureToDelete?.feature_name?.trim();
      
      if (hadContent) {
        onDirtyChange?.(true);
        onChange?.({ features });
      }
      
      return { ...p, features };
    });
  };

  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  const handleLocalPropertyListCreated = (newList: { id: number; list_name: string }) => {
    setLocalPropertyLists((prev) => {
      const exists = prev.some((l) => l.id === newList.id);
      if (exists) return prev;
      return [...prev, newList];
    });
    
    setSelectedList(newList.list_name);
    setData((prev) => ({ ...prev, list_name: newList.list_name }));
    
    onPropertyListCreated?.(newList);
    onPropertyListChange?.(newList.list_name);
  };

  const updateList = async (newList: string) => {
    if (newList === selectedList) {
      return;
    }

    setSelectedList(newList);
    setData((prev) => ({ ...prev, list_name: newList }));
    onDirtyChange?.(true);

    const list = localPropertyLists.find((l) => l.list_name === newList);

    if (property?.id && list?.id) {
      const payload: any = {};

      if (property.propertyListType?.id !== list.id) {
        payload.propertyListType_id = list.id;
      }

      if (Object.keys(payload).length > 0) {
        try {
          await axiosInstance.patch(`${endpoints.properties}${property.id}/`, payload);
          onPropertyListChange?.(newList);
          toast({ title: "Success", description: "Property list updated" });
        } catch (err) {
          toast({
            title: "Error",
            description: "Failed to update property list",
            variant: "destructive",
          });
        }
      }
    }
  };

  const noop = () => {};
  const noopFile = async (_e: React.ChangeEvent<HTMLInputElement>) => {};

  return (
    <div>
      <View
        property={property}
        propertyLists={localPropertyLists}
        keyFeatures={keyFeatures}
        onDirtyChange={readOnly ? undefined : onDirtyChange}
        onChange={readOnly ? undefined : onChange}
        onPropertyListChange={readOnly ? undefined : onPropertyListChange}
        onPropertyListCreated={readOnly ? undefined : handleLocalPropertyListCreated}
        onPropertyListsUpdate={readOnly ? undefined : onPropertyListsUpdate}
        view={View}
        data={data}
        selectedList={selectedList}
        fileInputRef={fileInputRef}
        onInputChange={readOnly ? noop : handleInput}
        onFileChange={readOnly ? noopFile : handleFile}
        onAddFeature={readOnly ? noop : addFeature}
        onFeatureChange={readOnly ? noop : changeFeature}
        onFeatureBlur={readOnly ? noop : blurFeature}
        onFeatureDelete={readOnly ? noop : deleteFeature}
        onListChange={readOnly ? (async () => {}) : updateList}
        isLoading={false}
        error={null}
        readOnly={readOnly}
        isOpenHouseConfirmed={selectedList === "Open House" ? isOpenHouseConfirmed : false}
        onOpenHouseConfirm={readOnly ? async () => {} : handleOpenHouseConfirm}
      />
    </div>
  );
};

export default FlyerTemplate;